import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';

export default function AddColumnPanel({ allFields, visibleKeys, onAddColumn }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const hidden = allFields.filter(f => !visibleKeys.has(f.key));
    const filtered = hidden.filter(f =>
        f.label.toLowerCase().includes(search.toLowerCase()) ||
        f.key.toLowerCase().includes(search.toLowerCase())
    );

    // Group by category
    const standard = filtered.filter(f => !f.isCustom && !f.computed);
    const computed = filtered.filter(f => f.computed);
    const custom = filtered.filter(f => f.isCustom);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1 px-2 py-1.5 text-xs text-stone-400 hover:text-stone-700 hover:bg-stone-50 rounded-lg transition-colors whitespace-nowrap"
            >
                <Plus className="w-3.5 h-3.5" /> Add column
            </button>

            {open && (
                <div className="absolute top-full right-0 mt-1 w-64 bg-white rounded-lg border border-stone-200 shadow-lg z-50">
                    <div className="p-2 border-b border-stone-100">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search attributes..."
                                className="w-full pl-7 pr-2 py-1.5 text-xs border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-stone-400"
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                        {standard.length > 0 && (
                            <>
                                <div className="px-3 py-1 text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Standard</div>
                                {standard.map(f => (
                                    <button key={f.key} onClick={() => { onAddColumn(f.key); setOpen(false); setSearch(''); }}
                                        className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 transition-colors">
                                        {f.label}
                                    </button>
                                ))}
                            </>
                        )}
                        {computed.length > 0 && (
                            <>
                                <div className="px-3 py-1 text-[10px] font-semibold text-stone-400 uppercase tracking-wider mt-1">Computed</div>
                                {computed.map(f => (
                                    <button key={f.key} onClick={() => { onAddColumn(f.key); setOpen(false); setSearch(''); }}
                                        className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 transition-colors">
                                        {f.label}
                                    </button>
                                ))}
                            </>
                        )}
                        {custom.length > 0 && (
                            <>
                                <div className="px-3 py-1 text-[10px] font-semibold text-stone-400 uppercase tracking-wider mt-1">Custom</div>
                                {custom.map(f => (
                                    <button key={f.key} onClick={() => { onAddColumn(f.key); setOpen(false); setSearch(''); }}
                                        className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 transition-colors">
                                        {f.label}
                                    </button>
                                ))}
                            </>
                        )}
                        {filtered.length === 0 && (
                            <p className="text-xs text-stone-400 text-center py-3">All columns visible</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
